import { catchError, concatMap, map, of, tap, withLatestFrom } from 'rxjs';

import { Location } from '@angular/common';
import {
  CRUDApiClientInterface,
  LoaderFacadeInterface,
  RouterFacadeInterface,
  ToastServiceInterface,
} from '@ideapa/app-common';
import { BaseDto } from '@ideapa/common-dto';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { fetch, pessimisticUpdate } from '@ngrx/router-store/data-persistence';

import { SharedEntityActions } from './models';

export abstract class BaseSharedEntityEffects<T extends BaseDto, V> {
  constructor(
    protected actions$: Actions,
    protected service: CRUDApiClientInterface<T>,
    protected loaderFacade: LoaderFacadeInterface,
    protected toastService: ToastServiceInterface,
    protected location: Location,
    protected routerFacade: RouterFacadeInterface,
    protected entityName: string,
    protected actions: SharedEntityActions<T>
  ) {}

  loadPaged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(this.actions.loadPaged),
      // switchMap(a => of(a).pipe(withLatestFrom(this.facade.pageSize$))),
      fetch({
        run: (action) => {
          this.loaderFacade.showLoader(action);
          // Your custom service 'load' logic goes here. For now just return a success action...
          return this.service
            .getPaged(
              action.take,
              action.skip,
              action.orderBy,
              action.filters,
              action.join
            )
            .pipe(
              map((res) => {
                this.loaderFacade.hideLoader(action);
                return this.actions.loadPagedSuccess({
                  items: res.items,
                });
              })
            );
        },
        onError: (action, error) => {
          this.loaderFacade.hideLoader(action);
          return this.actions.loadPagedFailure({ error });
        },
      })
    )
  );

  loadAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(this.actions.loadAll),
      fetch({
        run: (action) => {
          this.loaderFacade.showLoader(action);
          // Your custom service 'load' logic goes here. For now just return a success action...
          return this.service.getAll().pipe(
            map((res) => {
              this.loaderFacade.hideLoader(action);
              return this.actions.loadAllSuccess({ items: res });
            })
          );
        },
        onError: (action, error) => {
          this.loaderFacade.hideLoader(action);
          return this.actions.loadAllFailure({ error });
        },
      })
    )
  );

  loadSingle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(this.actions.loadSingle),
      fetch({
        id: (a) => {
          return a.id;
        },
        run: (action) => {
          this.loaderFacade.showLoader(action);
          // Your custom service 'load' logic goes here. For now just return a success action...
          return this.service.getById(action.id).pipe(
            map((res) => {
              this.loaderFacade.hideLoader(action);
              return this.actions.loadSingleSuccess({ item: res });
            })
          );
        },
        onError: (action, error) => {
          this.loaderFacade.hideLoader(action);
          return this.actions.loadSingleFailure({ error });
        },
      })
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(this.actions.update),
      pessimisticUpdate({
        // provides an action
        run: (action) => {
          this.loaderFacade.showLoader(action);
          // update the backend first, and then dispatch an action that will
          // update the client side
          return this.service.update(action.update).pipe(
            map((updated) => {
              this.loaderFacade.hideLoader(action);
              return this.actions.updateSuccess({
                item: updated,
                notify: action.notify,
                goBack: action.goBack,
                callback: action.callback,
              });
            })
          );
        },
        onError: (action, error) => {
          // we don't need to undo the changes on the client side.
          // we can dispatch an error, or simply log the error here and return `null`
          this.loaderFacade.hideLoader(action);
          return this.actions.updateFailure({
            error,
            notify: false, // gli errori vengono gestiti globalmente da HttpErrorInterceptor
          });
        },
      })
    )
  );

  updateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(this.actions.updateSuccess),
        concatMap((a) =>
          of(a).pipe(withLatestFrom(this.routerFacade.channelId$))
        ),
        tap(([action, channelId]) => {
          if (channelId) {
            this.routerFacade.notifySelectedItemToOpener(
              action.item,
              channelId
            );
          }
          if (action.notify) {
            this.toastService.successFeedback(
              'Aggiornamento avvenuto con successo!'
            );
          }
          if (action.callback) {
            action.callback(action.item);
          }
          if (action.goBack) {
            this.location.back();
          }
        })
      ),
    { dispatch: false }
  );

  updateFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(this.actions.updateFailure),
        tap((action) => {
          console.log(
            "Si è verificato un errore durante l'aggiornamento",
            action.error
          );
          if (action.notify) {
            this.toastService.errorFeedback(
              "Si è verificato un errore durante l'aggiornamento",
              action.error
            );
          }
        })
      ),
    { dispatch: false }
  );

  add$ = createEffect(() =>
    this.actions$.pipe(
      ofType(this.actions.add),
      pessimisticUpdate({
        // provides an action
        run: (action) => {
          this.loaderFacade.showLoader(action);
          // update the backend first, and then dispatch an action that will
          // update the client side
          return this.service.add(action.item).pipe(
            map((updated) => {
              this.loaderFacade.hideLoader(action);
              return this.actions.addSuccess({
                item: updated,
                notify: action.notify,
                goBack: action.goBack,
              });
            })
          );
        },
        onError: (action, error) => {
          // we don't need to undo the changes on the client side.
          // we can dispatch an error, or simply log the error here and return `null`
          this.loaderFacade.hideLoader(action);
          return this.actions.addFailure({
            error,
            notify: false, // gli errori vengono gestiti globalmente da HttpErrorInterceptor
          });
        },
      })
    )
  );

  addSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(this.actions.addSuccess),
        concatMap((a) =>
          of(a).pipe(withLatestFrom(this.routerFacade.channelId$))
        ),
        tap(([action, channelId]) => {
          if (channelId) {
            this.routerFacade.notifySelectedItemToOpener(
              action.item,
              channelId
            );
          }
          if (action.notify) {
            this.toastService.successFeedback(
              'Inserimento avvenuto con successo!'
            );
          }
          if (action.goBack) {
            this.location.back();
          } else {
            this.routerFacade.navigateRelative(['../', action.item.id], true);
          }
        })
      ),
    { dispatch: false }
  );

  addFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(this.actions.addFailure),
        tap((action) => {
          console.log(
            "Si è verificato un errore durante l'inserimento",
            action.error
          );
          if (action.notify) {
            this.toastService.errorFeedback(
              "Si è verificato un errore durante l'inserimento",
              action.error
            );
          }
        })
      ),
    { dispatch: false }
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(this.actions.deleteEntity),
      pessimisticUpdate({
        // provides an action
        run: (action) => {
          this.loaderFacade.showLoader(action);
          // update the backend first, and then dispatch an action that will
          // update the client side
          return this.service.delete(action.id).pipe(
            map(() => {
              this.loaderFacade.hideLoader(action);
              return this.actions.deleteEntitySuccess({
                id: action.id,
                notify: action.notify,
                goBack: action.goBack,
                callback: action.callback,
              });
            })
          );
        },
        onError: (action, error) => {
          // we don't need to undo the changes on the client side.
          // we can dispatch an error, or simply log the error here and return `null`
          this.loaderFacade.hideLoader(action);
          return this.actions.deleteEntityFailure({
            error: error.message,
            notify: false, // gli errori vengono gestiti globalmente da HttpErrorInterceptor
          });
        },
      })
    )
  );

  deleteError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(this.actions.deleteEntityFailure),
        tap((action) => {
          console.log(
            "Si è verificato un errore durante l'eliminazione",
            action.error
          );
          if (action.notify) {
            this.toastService.errorFeedback(
              "Si è verificato un errore durante l'eliminazione"
            );
          }
        })
      ),
    { dispatch: false }
  );

  deleteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(this.actions.deleteEntitySuccess),
        concatMap((a) =>
          of(a).pipe(withLatestFrom(this.routerFacade.channelId$))
        ),
        tap(([action, channelId]) => {
          if (channelId) {
            this.routerFacade.notifyDeletedItemToOpener(action.id, channelId);
          }
          if (action.notify) {
            this.toastService.successFeedback(
              'Eliminazione avvenuta con successo!'
            );
          }
          // se la pagina non c'è più, deve andare alla precedente o alla prima?
          if (action.callback) {
            action.callback();
          }
          if (action.goBack) {
            this.location.back();
          }
        })
      ),
    { dispatch: false }
  );

  downloadExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(this.actions.downloadExcel),
      fetch({
        run: (action) => {
          this.loaderFacade.showLoader(action);
          console.log('downloadExcel effects');
          return this.service
            .downloadExcel(
              action.fileName,
              action.columns,
              action.filters,
              action.orderBy,
              action.join
            )
            .pipe(
              map((res) => {
                const blob = new Blob([res], {
                  type: 'application/xlsx',
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${action.fileName}.xlsx`;
                document.body.appendChild(link);
                link.click();

                URL.revokeObjectURL(url);
                this.loaderFacade.hideLoader(action);
                return this.actions.downloadExcelSuccess({
                  responseFile: res,
                  notify: action.notify,
                });
              }),
              catchError((error) => {
                console.log(error);
                this.loaderFacade.hideLoader(action);
                return of(
                  this.actions.downloadExcelFailure({
                    error: error.message,
                    notify: false, // gli errori vengono gestiti globalmente da HttpErrorInterceptor
                  })
                );
              })
            );
        },
      })
    )
  );

  downloadExcelSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(this.actions.downloadExcelSuccess),
        tap((action) => {
          console.log('Download Excel avvenuto con successo');
          if (action.notify) {
            this.toastService.successFeedback(
              'Download Excel avvenuto con successo!'
            );
          }
        })
      ),
    { dispatch: false }
  );

  downloadExcelFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(this.actions.downloadExcelFailure),
        tap((action) => {
          console.log(
            'Si è verificato un errore durante il download',
            action.error
          );
          if (action.notify) {
            this.toastService.errorFeedback(
              'Si è verificato un errore durante il download',
              action.error
            );
          }
        })
      ),
    { dispatch: false }
  );

  clearCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(this.actions.clearCache),
        tap(() => {
          this.service.clearCache();
        })
      ),
    { dispatch: false }
  );
}
